import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Quote, Loader2, Send, Trash2 } from "lucide-react";
import { API } from "../../lib/site";
import { useAdminAuth } from "../../context/AdminAuth";
import { Reveal } from "../site/Reveal";

const inputClass =
  "w-full h-12 px-4 rounded-lg bg-forest border border-white/10 text-cream placeholder:text-cream/40 font-body focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors duration-200";

const Stars = ({ value, size = "h-4 w-4" }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-brand-orange text-brand-orange" : "text-cream/25"}`} />
    ))}
  </span>
);

const initials = (name) => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export const Reviews = () => {
  const { isAdmin, authHeader } = useAdminAuth();
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", rating: 0, comment: "" });
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`, { headers: authHeader() });
      toast.success("Review removed.");
      load();
    } catch {
      toast.error("Could not remove review.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.comment) return toast.error("Please add your name, email and a comment.");
    if (form.rating < 1) return toast.error("Please select a star rating.");
    setBusy(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you for your review!");
      setForm({ name: "", email: "", rating: 0, comment: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="reviews" className="relative bg-forest py-24 md:py-32 scroll-mt-20" data-testid="reviews-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="max-w-2xl mb-12">
          <span className="font-display uppercase tracking-[0.25em] text-xs text-brand-orange">Testimonials</span>
          <h2 className="mt-4 font-display uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl text-cream leading-[0.95]">
            What our <span className="text-brand-orange">clients say</span>
          </h2>
          {data.count > 0 && (
            <div className="mt-6 flex items-center gap-4" data-testid="reviews-average">
              <span className="font-display text-5xl text-cream leading-none">{data.average}</span>
              <div>
                <Stars value={data.average} size="h-5 w-5" />
                <p className="text-cream/60 text-sm mt-1">from {data.count} review{data.count !== 1 ? "s" : ""}</p>
              </div>
            </div>
          )}
        </Reveal>

        <div className="grid gap-8 lg:grid-cols-12">
          {/* Review list */}
          <div className="lg:col-span-7">
            {loading ? (
              <div className="py-16 text-center text-cream/50"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
            ) : data.reviews.length === 0 ? (
              <div className="py-16 text-center rounded-2xl border border-dashed border-white/15" data-testid="reviews-empty">
                <Quote className="h-10 w-10 text-cream/30 mx-auto mb-3" />
                <p className="text-cream/60">Be the first to leave a review!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {data.reviews.map((r, i) => (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.05, 0.3) }}
                    className="rounded-2xl border border-white/10 bg-forest-surface p-6" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-4 mb-3">
                      <span className="grid place-items-center h-12 w-12 rounded-full bg-brand-orange/20 text-brand-orange font-display text-lg">{initials(r.name)}</span>
                      <div className="flex-1">
                        <p className="font-display uppercase tracking-tight text-cream text-lg leading-none">{r.name}</p>
                        <div className="mt-1"><Stars value={r.rating} /></div>
                      </div>
                      {isAdmin && (
                        <button onClick={() => handleDelete(r.id)} className="grid place-items-center h-9 w-9 rounded-full bg-red-600/90 hover:bg-red-600 text-white transition-colors shrink-0" data-testid={`review-delete-${r.id}`} aria-label="Delete review">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <p className="text-cream/75 leading-relaxed">{r.comment}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Review form */}
          <Reveal delay={0.1} className="lg:col-span-5">
            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-forest-surface p-6 sm:p-8 lg:sticky lg:top-28" data-testid="review-form">
              <h3 className="font-display uppercase tracking-tight text-2xl text-cream">Leave a review</h3>
              <p className="text-cream/55 text-sm mt-1 mb-5">Sign in with your name & email to share your experience.</p>
              <div className="space-y-3">
                <input className={inputClass} placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="review-name" />
                <input className={inputClass} type="email" placeholder="Your email *" value={form.email} onChange={set("email")} data-testid="review-email" />
                <div className="flex items-center gap-2 h-12 px-1" data-testid="review-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setForm((f) => ({ ...f, rating: n }))}
                      className="p-1 transition-transform duration-150 hover:scale-110" data-testid={`review-star-${n}`} aria-label={`${n} star`}>
                      <Star className={`h-8 w-8 ${n <= (hover || form.rating) ? "fill-brand-orange text-brand-orange" : "text-cream/25"}`} />
                    </button>
                  ))}
                </div>
                <textarea rows={4} className={`${inputClass} h-auto py-3 resize-none`} placeholder="Tell us about your experience *" value={form.comment} onChange={set("comment")} data-testid="review-comment" />
              </div>
              <button type="submit" disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 h-13 py-3.5 rounded-full bg-brand-orange hover:bg-brand-orangeHover disabled:opacity-60 text-white font-display uppercase tracking-wider transition-[background-color,transform] duration-300 hover:-translate-y-0.5" data-testid="review-submit">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-4 w-4" /> Post Review</>}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
